import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCart = useCallback(async () => {
    if (!token) { setItems([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cart`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) {
      console.error('Cart fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (itemType, itemId, quantity = 1, opts = {}) => {
    try {
      await axios.post(`${API}/cart/add`, {
        item_type: itemType, item_id: itemId, quantity, ...opts
      }, { headers });
      await fetchCart();
      return true;
    } catch (e) {
      console.error('Add to cart error:', e);
      return false;
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    try {
      await axios.put(`${API}/cart/${itemId}`, { quantity }, { headers });
      await fetchCart();
    } catch (e) { console.error(e); }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`, { headers });
      await fetchCart();
    } catch (e) { console.error(e); }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`, { headers });
      setItems([]);
    } catch (e) { console.error(e); }
  };

  const total = items.reduce((sum, item) => sum + (item.item_details?.price || 0) * item.quantity, 0);

  return (
    <CartContext.Provider value={{ items, loading, addToCart, updateQuantity, removeItem, clearCart, total, fetchCart, itemCount: items.length }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
